import { useEffect, useState } from 'react';
import { HFBox, HFButton, HFTooltip, PauseIcon, PlayArrowIcon, useHfTheme } from '@hf/designSystem/v1';
import { formatAsOf, formatAsOfClock, omsLiveState, type OmsLiveState } from './omsStatus';

function sameLocalDay(iso: string, now: number): boolean {
  const date = new Date(iso);
  const current = new Date(now);
  if (Number.isNaN(date.getTime())) return false;
  return date.getFullYear() === current.getFullYear() && date.getMonth() === current.getMonth() && date.getDate() === current.getDate();
}

const LABEL: Record<OmsLiveState, string> = {
  live: 'LIVE',
  stale: 'STALE',
  offline: 'OFFLINE',
  paused: 'PAUSED',
};

/** Tab-bar freshness and the environment badge. Pause is a separate control. */
export function OmsTabStatus({
  asOf,
  failures,
  badge,
  paused = false,
  refreshPaused = false,
  onTogglePause,
  freshness = true,
}: {
  asOf: string | null;
  failures: number;
  badge: string | null;
  paused?: boolean;
  refreshPaused?: boolean;
  onTogglePause?: () => void;
  /** False for a tab that only reads on demand: LIVE and STALE are hidden, OFFLINE still shows. */
  freshness?: boolean;
}) {
  const theme = useHfTheme();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const liveState = omsLiveState({ asOf, failures, now, paused });
  const state = !freshness && (liveState === 'live' || liveState === 'stale') ? null : liveState;
  const stamp = asOf ? formatAsOf(asOf) : null;
  const clock = asOf ? formatAsOfClock(asOf) : null;
  const asOfToday = asOf ? sameLocalDay(asOf, now) : false;
  const asOfLabel = stamp ? `Last refreshed ${asOfToday && clock ? clock : stamp}` : null;
  const color =
    state === 'live'
      ? theme.palette.success.main
      : state === 'stale'
        ? theme.palette.warning.main
        : state === 'paused'
          ? theme.palette.text.secondary
          : theme.palette.error.main;
  if (!badge && !state && !asOfLabel && !onTogglePause) return null;

  return (
    <HFBox sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      {badge ? (
        <HFBox
          component="span"
          sx={{
            px: 1.25,
            py: 0.5,
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.06em',
            color: 'warning.contrastText',
            bgcolor: 'warning.main',
          }}
        >
          {badge}
        </HFBox>
      ) : null}
      {state ? (
        <HFBox
          component="span"
          role="status"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '6px',
            fontSize: 12,
            minWidth: 'calc(8px + 6px + 7ch)',
          }}
        >
          <HFBox component="span" aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
          <HFBox component="span" sx={{ fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color }}>
            {LABEL[state]}
          </HFBox>
        </HFBox>
      ) : null}
      {state && asOfLabel ? <HFBox aria-hidden sx={{ width: '1px', height: 14, bgcolor: 'divider', mx: 0.25 }} /> : null}
      {asOfLabel ? (
        <HFTooltip title={asOfToday && stamp ? stamp : undefined} disableHoverListener={!asOfToday}>
          <HFBox
            component="span"
            sx={{ fontSize: 13, fontWeight: 400, color: 'text.secondary', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
          >
            {asOfLabel}
          </HFBox>
        </HFTooltip>
      ) : null}
      {onTogglePause ? (
        <HFTooltip title={refreshPaused ? 'Resume refresh' : 'Pause refresh'}>
          <HFButton
            variant="outlined"
            size="small"
            aria-label={refreshPaused ? 'Resume refresh' : 'Pause refresh'}
            aria-pressed={refreshPaused}
            onClick={onTogglePause}
            sx={{
              minWidth: 28,
              width: 28,
              height: 28,
              minHeight: 28,
              p: 0,
              borderRadius: '6px',
              borderColor: 'divider',
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover', borderColor: 'divider' },
            }}
          >
            {refreshPaused ? <PlayArrowIcon sx={{ fontSize: 18 }} /> : <PauseIcon sx={{ fontSize: 18 }} />}
          </HFButton>
        </HFTooltip>
      ) : null}
    </HFBox>
  );
}
